import { useEffect, useState } from "react";
import { Rocket, CheckCircle2, AlertTriangle } from "lucide-react";
import AdminShell, { Panel, Btn } from "@/components/admin/AdminShell";
import { api, getErrorMessage } from "@/lib/api";
import { DEFAULT_LAUNCH_GATE_MESSAGE } from "@/lib/launchGateCopy";
import { syncLaunchGateFromAdmin } from "@/lib/useLaunchGate";

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function AdminLaunchGate() {
  const [enabled, setEnabled] = useState(false);
  const [popupEnabled, setPopupEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [launchLocal, setLaunchLocal] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);
  const [savingPopupToggle, setSavingPopupToggle] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/api/admin/launch-gate");
      setEnabled(Boolean(data?.enabled));
      setPopupEnabled(Boolean(data?.popup_enabled));
      setMessage(data?.message || "");
      setLaunchLocal(toLocalInputValue(data?.launch_at));
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function onToggle() {
    setSavingToggle(true);
    setErr("");
    setOk("");
    try {
      const { data } = await api.put("/api/admin/launch-gate", { enabled: !enabled });
      setEnabled(Boolean(data?.enabled));
      setPopupEnabled(Boolean(data?.popup_enabled));
      syncLaunchGateFromAdmin(data);
      setOk(data?.enabled ? "Full-site gate enabled." : "Full-site gate disabled.");
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setSavingToggle(false);
    }
  }

  async function onPopupToggle() {
    setSavingPopupToggle(true);
    setErr("");
    setOk("");
    try {
      const { data } = await api.put("/api/admin/launch-gate", { popup_enabled: !popupEnabled });
      setPopupEnabled(Boolean(data?.popup_enabled));
      syncLaunchGateFromAdmin(data);
      setOk(data?.popup_enabled ? "Coming Soon popup enabled." : "Coming Soon popup disabled.");
    } catch (e) {
      setErr(getErrorMessage(e));
    } finally {
      setSavingPopupToggle(false);
    }
  }

  async function onSaveContent(e) {
    e.preventDefault();
    setSavingContent(true);
    setErr("");
    setOk("");
    try {
      const { data } = await api.put("/api/admin/launch-gate", {
        message: message.trim(),
        launch_at: localInputToIso(launchLocal),
      });
      setMessage(data?.message || "");
      setPopupEnabled(Boolean(data?.popup_enabled));
      setLaunchLocal(toLocalInputValue(data?.launch_at));
      syncLaunchGateFromAdmin(data);
      setOk("Message and launch date saved.");
    } catch (e2) {
      setErr(getErrorMessage(e2));
    } finally {
      setSavingContent(false);
    }
  }

  return (
    <AdminShell
      title="Coming Soon — Launch Gate"
      subtitle="Control the full-site gate or a dismissible Coming Soon popup. Message and countdown are shared between both."
    >
      {loading ? (
        <Panel className="p-10 text-center text-[#8892A8]">Loading…</Panel>
      ) : (
        <div className="space-y-6 max-w-[900px]">
          <Panel>
            <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 border-b border-[#1E2845]">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[15px] font-bold text-[#E5E9F2]">
                  <Rocket size={18} className="text-[#F0B83A]" />
                  Full-site gate
                </div>
                <p className="mt-2 text-[13px] text-[#8892A8] leading-relaxed max-w-[520px]">
                  When <strong className="text-[#E5E9F2]">ON</strong>, visitors cannot browse the site —
                  only the full-screen Coming Soon page is shown. Admin routes stay accessible.
                </p>
                <div
                  className={`mt-4 inline-flex items-center gap-2 text-[13px] font-semibold ${
                    enabled ? "text-[#F59E0B]" : "text-[#10B981]"
                  }`}
                  data-testid="launch-gate-status"
                >
                  {enabled ? (
                    <>
                      <AlertTriangle size={16} />
                      Site locked — full Coming Soon page only
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={16} />
                      Site is open (popup may still show if enabled below)
                    </>
                  )}
                </div>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                disabled={savingToggle}
                onClick={onToggle}
                data-testid="launch-gate-toggle"
                className={`relative shrink-0 w-[72px] h-[40px] rounded-full transition-colors ${
                  enabled ? "bg-[#F0B83A]" : "bg-[#1E2845]"
                } ${savingToggle ? "opacity-60" : ""}`}
              >
                <span
                  className={`absolute top-1 left-1 w-[32px] h-[32px] rounded-full bg-white shadow transition-transform ${
                    enabled ? "translate-x-[32px]" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[15px] font-bold text-[#E5E9F2]">
                  <Rocket size={18} className="text-[#022CED]" />
                  Coming Soon popup
                </div>
                <p className="mt-2 text-[13px] text-[#8892A8] leading-relaxed max-w-[520px]">
                  When <strong className="text-[#E5E9F2]">ON</strong>, visitors see a centered modal on the
                  homepage only (each visit / refresh until dismissed). Ignored while full-site gate is on.
                </p>
                <div
                  className={`mt-4 inline-flex items-center gap-2 text-[13px] font-semibold ${
                    popupEnabled ? "text-[#60A5FA]" : "text-[#8892A8]"
                  }`}
                  data-testid="launch-gate-popup-status"
                >
                  {popupEnabled ? (
                    <>
                      <CheckCircle2 size={16} />
                      Popup will show until visitor dismisses it
                    </>
                  ) : (
                    <>Popup is off</>
                  )}
                </div>
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={popupEnabled}
                disabled={savingPopupToggle || enabled}
                onClick={onPopupToggle}
                data-testid="launch-gate-popup-toggle"
                title={enabled ? "Disable full-site gate first" : undefined}
                className={`relative shrink-0 w-[72px] h-[40px] rounded-full transition-colors ${
                  popupEnabled ? "bg-[#022CED]" : "bg-[#1E2845]"
                } ${savingPopupToggle || enabled ? "opacity-60" : ""}`}
              >
                <span
                  className={`absolute top-1 left-1 w-[32px] h-[32px] rounded-full bg-white shadow transition-transform ${
                    popupEnabled ? "translate-x-[32px]" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </Panel>

          <form onSubmit={onSaveContent}>
            <div className="grid lg:grid-cols-2 gap-5">
              <Panel className="p-5">
                <label className="block text-[12px] font-bold uppercase tracking-wider text-[#8892A8] mb-2">
                  Custom message (optional)
                </label>
                <textarea
                  rows={5}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={DEFAULT_LAUNCH_GATE_MESSAGE}
                  className="w-full rounded-[10px] border border-[#1E2845] bg-[#0A0F1F] px-3 py-2.5 text-[14px] text-[#E5E9F2] outline-none focus:border-[#F0B83A]/50 resize-y"
                  data-testid="launch-gate-message"
                />
              </Panel>

              <Panel className="p-5">
                <label className="block text-[12px] font-bold uppercase tracking-wider text-[#8892A8] mb-2">
                  Launch date / time (optional — drives countdown clock)
                </label>
                <input
                  type="datetime-local"
                  value={launchLocal}
                  onChange={(e) => setLaunchLocal(e.target.value)}
                  className="w-full h-[42px] rounded-[10px] border border-[#1E2845] bg-[#0A0F1F] px-3 text-[14px] text-[#E5E9F2] outline-none focus:border-[#F0B83A]/50"
                  data-testid="launch-gate-datetime"
                />
                <p className="mt-2 text-[12px] text-[#8892A8]">
                  Leave blank to hide the countdown timer on the page.
                </p>
              </Panel>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-4">
              <Btn type="submit" disabled={savingContent} data-testid="launch-gate-save">
                {savingContent ? "Saving…" : "Save message & date"}
              </Btn>
              {ok && <span className="text-[13px] text-[#10B981]">{ok}</span>}
              {err && <span className="text-[13px] text-[#EF4444]">{err}</span>}
            </div>
          </form>
        </div>
      )}
    </AdminShell>
  );
}
